import { useCallback, useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { differenceWith } from "lodash";

import { yupResolver } from "@hookform/resolvers/yup";
import { yup } from "utils";

export const validationSchema = (allRequired = false) => {
  return {
    firstName: yup
      .string()
      .test(
        "isRequired",
        "First name is required",
        function firstNameValTest(value) {
          const { lastName, email } = this.parent;
          if (lastName || email || allRequired) {
            return Boolean(value);
          }
          return true;
        }
      ),
    lastName: yup
      .string()
      .test(
        "isRequired",
        "Last name is required",
        function lastNameValTest(value) {
          const { firstName, email } = this.parent;
          if (firstName || email || allRequired) {
            return Boolean(value);
          }
          return true;
        }
      ),
    email: yup
      .string()
      .email("Email format is not correct")
      .test("isRequired", "Email is required", function emailValTest(value) {
        const { firstName, lastName } = this.parent;
        if (firstName || lastName || allRequired) {
          return Boolean(value);
        }
        return true;
      }),
    inviteNow: yup
      .boolean()
      .test("isRequired", "Invite Now is required", (value) => {
        if (allRequired) {
          return typeof value === "boolean";
        }

        return true;
      }),
  };
};

const schema = yup.object().shape({
  newUserInfo: yup
    .array()
    .of(yup.object().shape(validationSchema()))
    .uniqueProperty("email", "Email must be unique"),
});

const useNewUserInfoForm = ({
  defaultNewUserInfo,
  defaultInviteNow = false,
}) => {
  const {
    control,
    setError,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty },
    clearErrors,
    getValues,
    reset,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: { newUserInfo: defaultNewUserInfo },
  });

  useEffect(() => {
    reset({ newUserInfo: defaultNewUserInfo });
  }, [defaultNewUserInfo, reset]);

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "newUserInfo",
  });

  const backfill = useCallback(
    (info) => {
      const d = {
        firstName: "",
        lastName: "",
        email: "",
        roleId: "",
        inviteNow: defaultInviteNow,
      };

      return (
        info?.map?.((i) => {
          return { ...d, ...i };
        }) || { ...d, ...info }
      );
    },
    [defaultInviteNow]
  );

  const appendBackfill = useCallback(
    (v) => {
      return append(backfill(v));
    },
    [append, backfill]
  );

  const uniqueFormValues = useCallback(
    (info) => {
      const currentValues = getValues("newUserInfo");
      return differenceWith(info, currentValues, (newV, currentV) => {
        const emptyIgnored = {
          firstName: newV.firstName || currentV.firstName,
          lastName: newV.lastName || currentV.lastName,
          email: newV.email || currentV.email,
        };

        return (
          emptyIgnored.firstName === currentV.firstName &&
          emptyIgnored.lastName === currentV.lastName &&
          emptyIgnored.email === currentV.email
        );
      });
    },
    [getValues]
  );

  const getPrunedUserInfo = useCallback(
    (defaultRole) => {
      const newUserInfo = getValues("newUserInfo");
      return newUserInfo
        .map((userInfo) => {
          return { ...userInfo, roleId: userInfo.roleId || defaultRole.id };
        })
        .filter(({ firstName, lastName, email }) => {
          return firstName && lastName && email;
        });
    },
    [getValues]
  );

  return {
    control: control,
    setError: setError,
    handleSubmit: handleSubmit,
    isSubmitting: isSubmitting,
    errors: errors,
    clearErrors: clearErrors,
    fields: fields,
    isDirty: isDirty,
    update: useCallback(
      (v) => {
        const formValues = Array.isArray(v) ? v : [v];
        appendBackfill(uniqueFormValues(formValues));
      },
      [uniqueFormValues, appendBackfill]
    ),
    append: appendBackfill,
    remove: remove,
    getPrunedUserInfo: getPrunedUserInfo,
  };
};

export default useNewUserInfoForm;
